import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import logo from "@/assets/brand/simbolo.svg";

const inputCls = "h-[42px] rounded-[10px] bg-muted text-[13.5px]";
const labelCls = "text-xs font-semibold text-muted-foreground";

const AdminResetPassword = () => {
  // Pronto quando o link de recuperação foi aberto (PASSWORD_RECOVERY) ou já há sessão.
  const { session, loading: authLoading, isPasswordRecovery, updatePassword } = useAuth();
  const ready: boolean | null = authLoading ? null : isPasswordRecovery || !!session;
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({
        title: "Senha muito curta",
        description: "Use pelo menos 6 caracteres.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({ title: "As senhas não conferem", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await updatePassword(password);
    setLoading(false);
    if (error) {
      toast({
        title: "Não foi possível atualizar",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Senha atualizada" });
    navigate("/admin", { replace: true });
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="flex w-full max-w-[400px] flex-col gap-[22px]">
        <div className="flex flex-col items-center gap-3.5">
          <img src={logo} alt="" className="h-[52px] w-[52px] object-contain" />
          <div className="text-center">
            <h1 className="mb-[5px] text-[22px] font-bold tracking-[-0.02em]">
              Definir nova senha
            </h1>
            <p className="text-[13px] text-muted-foreground">Área restrita · eliveutonsouza.com</p>
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-[26px] shadow-card">
          {ready === null ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !ready ? (
            <div className="flex flex-col gap-4 text-center">
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                Link inválido ou expirado. Peça um novo link de recuperação na tela de login.
              </p>
              <Link
                to="/admin/login"
                className="text-[12.5px] font-semibold text-primary hover:underline"
              >
                Ir para o login
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <div className="space-y-[7px]">
                <label className={labelCls} htmlFor="new-password">
                  Nova senha
                </label>
                <Input
                  id="new-password"
                  type="password"
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-[7px]">
                <label className={labelCls} htmlFor="confirm-password">
                  Confirmar senha
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  className={inputCls}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <Button
                type="submit"
                className="h-11 w-full rounded-[11px] text-[13.5px] font-bold"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar nova senha
              </Button>
            </form>
          )}
        </div>

        <Link
          to="/"
          className="text-center text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
        >
          ← Voltar ao site
        </Link>
      </div>
    </div>
  );
};

export default AdminResetPassword;
